import { useRef, useState, useEffect } from "react";
import { X, Eraser, Check } from "@phosphor-icons/react";

/* A self-contained signature canvas modal. Works offline. */
export default function SignatureModal({ open, onClose, onAccept, title = "Add signature", signerName = "" }) {
    const canvasRef = useRef(null);
    const [drawing, setDrawing] = useState(false);
    const [hasInk, setHasInk] = useState(false);
    const [name, setName] = useState(signerName);

    useEffect(() => {
        if (!open || !canvasRef.current) return;
        const ctx = canvasRef.current.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.strokeStyle = "#0E1A24";
        ctx.lineWidth = 2.4;
        ctx.lineCap = "round";
        setHasInk(false);
    }, [open]);

    if (!open) return null;

    const point = (e) => {
        const r = canvasRef.current.getBoundingClientRect();
        const t = e.touches ? e.touches[0] : e;
        return { x: t.clientX - r.left, y: t.clientY - r.top };
    };

    const start = (e) => {
        e.preventDefault();
        const ctx = canvasRef.current.getContext("2d");
        const p = point(e);
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        setDrawing(true);
    };
    const move = (e) => {
        if (!drawing) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext("2d");
        const p = point(e);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        setHasInk(true);
    };
    const end = () => setDrawing(false);

    const clear = () => {
        const ctx = canvasRef.current.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        setHasInk(false);
    };

    const accept = () => {
        if (!hasInk) return;
        const data = canvasRef.current.toDataURL("image/png");
        onAccept({ data_url: data, signed_by: name || signerName || "Signer" });
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="signature-modal">
            <div className="rom-card w-full max-w-2xl">
                <div className="rom-card-head">
                    <h3>{title}</h3>
                    <button onClick={onClose} className="p-1" data-testid="signature-close">
                        <X size={18} />
                    </button>
                </div>
                <div className="rom-card-body space-y-4">
                    <div>
                        <label className="label">Signer name</label>
                        <input
                            className="input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Full name"
                            data-testid="signature-name"
                        />
                    </div>
                    <div>
                        <label className="label">Draw signature below</label>
                        <div className="border-2 border-dashed border-[var(--rom-border-strong)] rounded-[6px] bg-white">
                            <canvas
                                ref={canvasRef}
                                width={640}
                                height={220}
                                className="w-full touch-none cursor-crosshair block"
                                style={{ height: 220 }}
                                onMouseDown={start}
                                onMouseMove={move}
                                onMouseUp={end}
                                onMouseLeave={end}
                                onTouchStart={start}
                                onTouchMove={move}
                                onTouchEnd={end}
                                data-testid="signature-canvas"
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-between">
                        <button className="btn btn-ghost" onClick={clear} data-testid="signature-clear">
                            <Eraser size={14} weight="bold" />
                            Clear
                        </button>
                        <div className="flex gap-2">
                            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
                            <button
                                className="btn btn-primary"
                                onClick={accept}
                                disabled={!hasInk}
                                data-testid="signature-accept"
                            >
                                <Check size={14} weight="bold" />
                                Accept signature
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
